import { useState, useCallback } from 'react';
import { useInvisibleCaptcha } from '../useInvisibleCaptcha';
import { SM_ERROR_CODES } from '../../Captcha.types';
import type { ICaptchaSettingsOption } from '../../../types';
import type { FormType } from '../../../../types/types';

/**
 * Status of the invisible captcha token retrieval attempt
 */
export type InvisibleCaptchaStatus =
  | 'NO_CAPTCHA_REQUIRED' // recaptchaOption is NEVER
  | 'VISIBLE_CAPTCHA_REQUIRED' // Fallback triggered or ALWAYS mode
  | 'SUCCESS'; // Token obtained successfully

/**
 * Result of attempting to get an invisible captcha token
 */
export interface InvisibleCaptchaResult {
  status: InvisibleCaptchaStatus;
  token?: string;
}

export interface UseCaptchaWithFallbackOptions {
  recaptchaOption: ICaptchaSettingsOption;
  isEditor: boolean;
  language: string;
  action: FormType;
}

export interface UseCaptchaWithFallbackReturn {
  /**
   * Whether to show the visible captcha widget
   */
  shouldShowVisibleCaptcha: boolean;
  /**
   * Attempt to get an invisible captcha token.
   * Returns a result object with explicit status and optional token.
   */
  tryGetInvisibleToken: () => Promise<InvisibleCaptchaResult>;
  /**
   * Check if an error code is a captcha error and trigger fallback if so.
   * Returns true if it was a captcha error.
   */
  handleCaptchaError: (errorCode: string | undefined) => boolean;
}

/**
 * Hook that manages captcha logic with automatic fallback from invisible to visible captcha.
 *
 * Handles:
 * - Determining whether to use invisible or visible captcha based on settings
 * - Getting invisible captcha tokens with automatic fallback on failure
 * - Detecting captcha errors from server responses and triggering fallback
 */
export const useCaptchaWithFallback = ({
  recaptchaOption,
  isEditor,
  language,
  action,
}: UseCaptchaWithFallbackOptions): UseCaptchaWithFallbackReturn => {
  const [visibleCaptchaRequired, setVisibleCaptchaRequired] = useState(false);

  // Only enable invisible captcha when it's explicitly needed.
  // - When recaptchaOption is 'ALWAYS', only visible captcha is used
  // - When recaptchaOption is undefined/UNKNOWN, we wait for the actual setting
  // - Only enable for 'SUSPECTED_BOTS_ONLY' which uses invisible captcha first
  const invisibleCaptchaEnabled = recaptchaOption === 'SUSPECTED_BOTS_ONLY';

  const { getToken } = useInvisibleCaptcha({
    language,
    action,
    enabled: invisibleCaptchaEnabled,
  });

  const shouldShowVisibleCaptcha =
    recaptchaOption === 'ALWAYS' ||
    visibleCaptchaRequired ||
    (isEditor && recaptchaOption !== 'NEVER');

  const tryGetInvisibleToken =
    useCallback(async (): Promise<InvisibleCaptchaResult> => {
      // No captcha needed at all
      if (recaptchaOption === 'NEVER') {
        return { status: 'NO_CAPTCHA_REQUIRED' };
      }

      // Use visible captcha when: always required, already triggered fallback, or in editor
      if (recaptchaOption === 'ALWAYS' || visibleCaptchaRequired || isEditor) {
        return { status: 'VISIBLE_CAPTCHA_REQUIRED' };
      }

      const token = await getToken();
      if (!token) {
        setVisibleCaptchaRequired(true);
        return { status: 'VISIBLE_CAPTCHA_REQUIRED' };
      }

      return { status: 'SUCCESS', token };
    }, [recaptchaOption, visibleCaptchaRequired, isEditor, getToken]);

  const handleCaptchaError = useCallback(
    (errorCode: string | undefined): boolean => {
      const isCaptchaError =
        errorCode === SM_ERROR_CODES.CAPTCHA_REQUIRED ||
        errorCode === SM_ERROR_CODES.CAPTCHA_INVALID;

      if (isCaptchaError) {
        setVisibleCaptchaRequired(true);
      }
      return isCaptchaError;
    },
    [],
  );

  return {
    shouldShowVisibleCaptcha,
    tryGetInvisibleToken,
    handleCaptchaError,
  };
};
